import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, FileText } from 'lucide-react';
import { flyersService } from '../../services/flyersService';
import { Button } from '../../components/ui/Button';
import { FlyerPageView } from '../../components/flyer/FlyerPageView';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { formatDate } from '../../utils/helpers';

/**
 * Náhled letáku jen ke čtení pro admina a schvalovatelskou dvojici.
 *
 * Editor (`/flyers/:id`) je vyhrazený autorovi a uložené PDF má jen leták, který
 * už byl někdy vygenerovaný - u rozpracovaného konceptu tedy nebylo co otevřít.
 * Kreslí se stejnou komponentou jako náhled ve schvalování, takže schvalovatel
 * vidí leták v podobě, na jakou je zvyklý.
 *
 * Na leták prodejce se sem nedostane - backend ho těmto rolím nevydá.
 */
export const FlyerPreviewPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [stranka, setStranka] = useState(0);

  const { data: flyer, isLoading, error } = useQuery({
    queryKey: ['flyers', id],
    queryFn: () => flyersService.getFlyer(id!),
    enabled: !!id,
  });

  const zobrazitPdf = async () => {
    try {
      const blob = await flyersService.getPdfBlob(id!, true);
      window.open(URL.createObjectURL(blob), '_blank');
    } catch {
      alert('PDF se nepodařilo otevřít. Leták zřejmě zatím žádné uložené nemá.');
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error || !flyer) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Button variant="outline" onClick={() => navigate('/active-flyers')} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zpět na letáky
        </Button>
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Leták není k dispozici</h3>
          <p className="text-gray-600">Buď neexistuje, nebo k němu nemáte přístup.</p>
        </div>
      </div>
    );
  }

  const stranky = flyer.pages ?? [];
  const aktualni = stranky[Math.min(stranka, stranky.length - 1)];
  const autor = flyer.supplier
    ? [flyer.supplier.firstName, flyer.supplier.lastName].filter(Boolean).join(' ') || flyer.supplier.email
    : null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Button variant="outline" onClick={() => navigate('/active-flyers')} className="mb-4">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Zpět na letáky
      </Button>

      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-3 mb-2 min-w-0">
              <h1 className="text-2xl font-bold text-gray-900 truncate" title={flyer.name}>
                {flyer.name}
              </h1>
              <StatusBadge status={flyer.status} />
            </div>
            <div className="text-sm text-gray-600 space-y-1">
              <div>Platnost: {formatDate(flyer.validFrom)} – {formatDate(flyer.validTo)}</div>
              {autor && <div>Dodavatel: {autor}</div>}
              {flyer.actionName && <div>Akce: {flyer.actionName}</div>}
              <div>Stránky: {stranky.length}</div>
            </div>
          </div>
          <div className="flex-shrink-0">
            {flyer.hasPdf !== false && (
              <Button
                variant="outline"
                size="sm"
                onClick={zobrazitPdf}
                title="Uložené PDF – snímek z posledního generování"
              >
                <FileText className="w-4 h-4 mr-1" />
                PDF
              </Button>
            )}
          </div>
        </div>

        {flyer.rejectionReason && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm font-semibold text-red-900 mb-1">Důvod zamítnutí:</p>
            <p className="text-sm text-red-800 whitespace-pre-wrap break-words">{flyer.rejectionReason}</p>
          </div>
        )}

        <div className="mt-4 text-sm text-gray-500">
          Pouze k nahlédnutí – leták tu nejde upravovat. Kreslí se z <strong>aktuálních dat</strong>,
          takže se může lišit od uloženého PDF, pokud se od jeho vygenerování něco změnilo.
        </div>
      </div>

      {stranky.length === 0 || !aktualni ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Leták zatím nemá žádnou stránku</h3>
          <p className="text-gray-600">Dodavatel ho teprve začal skládat.</p>
        </div>
      ) : (
        <>
          <div className="flex justify-center mb-6">
            <FlyerPageView
              page={aktualni}
              pageIndex={Math.min(stranka, stranky.length - 1)}
              isEditable={false}
              validFrom={flyer.validFrom}
              validTo={flyer.validTo}
            />
          </div>

          {stranky.length > 1 && (
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between gap-4">
                <h3 className="font-bold whitespace-nowrap">
                  Strana {Math.min(stranka, stranky.length - 1) + 1} / {stranky.length}
                </h3>
                {/* Číslování se zalomí - leták může mít desítky stran. */}
                <div className="flex flex-wrap justify-end gap-1">
                  {stranky.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setStranka(i)}
                      className={`w-9 h-9 rounded text-sm ${
                        Math.min(stranka, stranky.length - 1) === i
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 hover:bg-gray-300'
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
